/** Shop Reports screen: period picker driving a paginated daily ledger of shop submissions, manual entry, and CSV export. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import {
  ApiError,
  type ReportImportRow,
  type ReportRow,
  type ReportsListResponse,
  type ReportType,
  type ShopSummary,
} from '../../api/index.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { ConfirmDialog } from '../../components/ConfirmDialog.tsx';
import { actionError } from '../../components/actionError.ts';
import { useCompact } from '../../hooks/useCompact.ts';
import { downloadBlob } from './csvDownload.ts';
import { ImportDialog } from './ImportDialog.tsx';
import { ImportHistoryPanel } from './ImportHistoryPanel.tsx';
import { PeriodFilter } from './PeriodFilter.tsx';
import { ReportFormDialog } from './ReportFormDialog.tsx';
import { ReportsTable } from './ReportsTable.tsx';

// Reference-equality sentinel: a fresh server response is never === this,
// so a genuinely empty period doesn't re-flash the first-load spinner.
export const EMPTY_LIST: ReportsListResponse = {
  page_info: { page: 1, limit: 10, total: 0 },
  data: [],
};

export function ReportsPage(): React.JSX.Element {
  const { api, hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const compact = useCompact();
  const canWrite = hasPermission('reports:write');
  // Gates the shop-name link in ReportsTable -- /shops/:id needs shops:read too.
  const canReadShops = hasPermission('shops:read');
  // v1 CSV import; the server enforces the same permission on every route.
  const canImport = hasPermission('reports:import');

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState<number | null>(now.getMonth() + 1);
  const [reportType, setReportType] = useState<ReportType>('live');
  // Single shop_id filter (server accepts only one); null = all shops.
  const requestedShopId = Number(searchParams.get('shop'));
  const initialShopId = Number.isInteger(requestedShopId) && requestedShopId > 0 ? requestedShopId : null;
  const [shopId, setShopId] = useState<number | null>(initialShopId);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [list, setList] = useState<ReportsListResponse>(EMPTY_LIST);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [shops, setShops] = useState<ShopSummary[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReport, setEditingReport] = useState<ReportRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReportRow | null>(null);

  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState('');

  const [importOpen, setImportOpen] = useState(false);
  const [imports, setImports] = useState<ReportImportRow[]>([]);
  const [importsLoading, setImportsLoading] = useState(canImport);
  const [importsError, setImportsError] = useState<string | null>(null);

  const effectiveType = canWrite ? reportType : undefined;

  // Sequence guard: the loader only applies the result of its most recent request.
  const listSeqRef = useRef(0);

  const loadList = useCallback(() => {
    const seq = ++listSeqRef.current;
    setListLoading(true);
    setListError(null);
    api.reports
      .list({
        year,
        month: month ?? undefined,
        report_type: effectiveType,
        shop_id: shopId ?? undefined,
        page,
        limit,
      })
      .then((data) => {
        if (listSeqRef.current !== seq) return;
        setList(data);
      })
      .catch((e: unknown) => {
        if (listSeqRef.current !== seq) return;
        setListError(actionError(e));
      })
      .finally(() => {
        if (listSeqRef.current !== seq) return;
        setListLoading(false);
      });
  }, [api, year, month, effectiveType, shopId, page, limit]);

  const loadImports = useCallback(() => {
    if (!canImport) return;
    setImportsLoading(true);
    setImportsError(null);
    api.reports
      .imports()
      .then((res) => setImports(res.data))
      .catch((e: unknown) => setImportsError(actionError(e)))
      .finally(() => setImportsLoading(false));
  }, [api, canImport]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    loadImports();
  }, [loadImports]);

  useEffect(() => {
    setShopId(initialShopId);
    setPage(1);
  }, [initialShopId]);

  useEffect(() => {
    // Feeds the add/edit dialog and the shop filter select; skip if neither applies.
    if (!canWrite && !canReadShops) return;
    api.shops
      .list()
      .then((res) => setShops(res.shops))
      .catch(() => {
        /* dialog/filter just show an empty shop list if this fails */
      });
  }, [api, canWrite, canReadShops]);

  function setYearAndResetPage(y: number) {
    setYear(y);
    setPage(1);
  }
  function setMonthAndResetPage(m: number | null) {
    setMonth(m);
    setPage(1);
  }
  function setReportTypeAndResetPage(t: ReportType) {
    setReportType(t);
    setPage(1);
  }
  function setShopAndResetPage(id: number | null) {
    setShopId(id);
    setPage(1);
    const next = new URLSearchParams(searchParams);
    if (id === null) next.delete('shop');
    else next.set('shop', String(id));
    setSearchParams(next);
  }

  function refreshAfterMutation() {
    loadList();
  }

  function openAdd() {
    setEditingReport(null);
    setDialogOpen(true);
  }
  function openEdit(row: ReportRow) {
    setEditingReport(row);
    setDialogOpen(true);
  }

  async function exportCsv() {
    if (month === null || csvBusy) return;
    setCsvBusy(true);
    setCsvError('');
    try {
      const { blob, filename } = await api.reports.csvExport({
        year,
        month,
        report_type: effectiveType,
        shop_id: shopId ?? undefined,
      });
      downloadBlob(blob, filename);
    } catch (e) {
      setCsvError(e instanceof ApiError ? actionError(e) : 'Could not export the CSV.');
    } finally {
      setCsvBusy(false);
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h5">Shop Reports</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={month === null || csvBusy}
            onClick={() => void exportCsv()}
          >
            Export CSV
          </Button>
          {canImport && (
            <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)}>
              Import CSV
            </Button>
          )}
          {canWrite && <Button onClick={openAdd}>Add report</Button>}
        </Box>
      </Box>

      <PeriodFilter
        year={year}
        month={month}
        reportType={reportType}
        showReportType={canWrite}
        onYearChange={setYearAndResetPage}
        onMonthChange={setMonthAndResetPage}
        onReportTypeChange={setReportTypeAndResetPage}
        shops={canReadShops ? shops : undefined}
        shopId={shopId}
        onShopChange={canReadShops ? setShopAndResetPage : undefined}
      />

      {csvError && (
        <Alert role="alert" severity="error" onClose={() => setCsvError('')}>
          {csvError}
        </Alert>
      )}

      <ReportsTable
        loading={listLoading}
        firstLoad={listLoading && list === EMPTY_LIST}
        error={listError}
        rows={list.data}
        pageInfo={list.page_info}
        canWrite={canWrite}
        shopLinkEnabled={canReadShops}
        compact={compact}
        onPageChange={setPage}
        onLimitChange={(l) => {
          setLimit(l);
          setPage(1);
        }}
        onEdit={openEdit}
        onDelete={setDeleteTarget}
      />

      {canImport && (
        <>
          <ImportHistoryPanel imports={imports} loading={importsLoading} error={importsError} />
          <ImportDialog
            open={importOpen}
            onClose={() => setImportOpen(false)}
            onApplied={() => {
              loadList();
              loadImports();
            }}
          />
        </>
      )}

      {canWrite && (
        <ReportFormDialog
          open={dialogOpen}
          report={editingReport}
          shops={shops}
          defaultReportType={reportType === 'live' ? 'live' : reportType}
          onClose={() => setDialogOpen(false)}
          onSaved={refreshAfterMutation}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete report"
        body={
          <Typography>
            Delete the report for {deleteTarget?.shop_name} on {deleteTarget?.submission_date}? This cannot be undone.
          </Typography>
        }
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={async () => {
          if (deleteTarget) await api.reports.remove(deleteTarget.id);
        }}
        onClose={() => setDeleteTarget(null)}
        onSuccess={refreshAfterMutation}
      />
    </Box>
  );
}

export default ReportsPage;
